// Client-side mirror of the server's provider-video rules
// (physio_server/src/business/welcomevideo.js). This exists so a clinician gets
// immediate feedback on a bad link; the server re-parses whatever is submitted
// and is the authority on what actually gets stored.

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/

const YOUTUBE_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]

const SHORT_HOSTS = ['youtu.be', 'www.youtu.be']

const ID_PATH_PREFIXES = ['embed', 'shorts', 'live', 'v']

// /embed/videoseries?list=... is YouTube's playlist embed. It is 11 legal
// characters, so it has to be excluded by name rather than by shape.
const RESERVED_IDS = ['videoseries']

// Returns the 11-character video id, or null when `value` is not a link to a
// single YouTube video. Playlist, channel and search URLs are rejected: there
// is no one video to play, so guessing would be worse than refusing.
const isVideoId = (value) =>
  !!value && YOUTUBE_ID.test(value) && !RESERVED_IDS.includes(value)

export const parseYouTubeId = (value) => {
  if (!value) return null

  const trimmed = String(value).trim()
  if (!trimmed) return null

  if (isVideoId(trimmed)) return trimmed

  let url
  try {
    url = new URL(trimmed)
  } catch (err) {
    return null
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

  const host = url.hostname.toLowerCase()
  const segments = url.pathname.split('/').filter(Boolean)

  if (SHORT_HOSTS.includes(host)) {
    if (segments.length !== 1) return null
    return isVideoId(segments[0]) ? segments[0] : null
  }

  if (!YOUTUBE_HOSTS.includes(host)) return null

  if (segments.length === 1 && segments[0] === 'watch') {
    const id = url.searchParams.get('v')
    return isVideoId(id) ? id : null
  }

  if (segments.length === 2 && ID_PATH_PREFIXES.includes(segments[0])) {
    return isVideoId(segments[1]) ? segments[1] : null
  }

  return null
}

// Canonical watch URL, used to show a stored id back to the clinician and for
// the admin-side preview.
export const youTubeWatchUrl = (id) =>
  id ? `https://www.youtube.com/watch?v=${id}` : ''

export default { parseYouTubeId, youTubeWatchUrl }
